import React, { useEffect, useRef } from 'react';
import { FileText } from 'lucide-react';

const TermsAndConditionsInput = ({ value, onChange, label = "Terms and Conditions", placeholder = "Enter terms and conditions..." }) => {
    const textareaRef = useRef(null);

    // Auto-resize textarea
    useEffect(() => {
        if (textareaRef.current) {
            textareaRef.current.style.height = 'auto';
            textareaRef.current.style.height = textareaRef.current.scrollHeight + 'px';
        }
    }, [value]);

    // Initial setup: If empty, add "1. "
    const handleFocus = () => {
        if (!value || value.trim() === '') {
            onChange('1. ');
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            const cursorPosition = e.target.selectionStart;
            const textBeforeCursor = value.substring(0, cursorPosition);
            const textAfterCursor = value.substring(cursorPosition);

            // Find the current line number
            const lines = value.split('\n');
            const currentLineIndex = value.substring(0, cursorPosition).split('\n').length - 1;
            const nextNumber = lines.length + 1;

            // Insert new line with incremented number
            const newValue = `${textBeforeCursor}\n${nextNumber}. ${textAfterCursor}`;

            onChange(newValue);

            // We need to set cursor position after render, but React state update is async.
            // A simple timeout helps here, or we could use another effect.
            // For simplicity, we rely on the user continuing to type.
            // Ideally, we'd calculate the new cursor position.
        }
    };

    // Better approach for Enter key: Handle auto-numbering logic carefully
    const handleChange = (e) => {
        // Enforce max length
        if (e.target.value.length <= 1000) {
            onChange(e.target.value);
        }
    };

    // Custom Enter key handler to ensure correct numbering sequence
    const handleKeyDownEnhanced = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();

            const start = e.target.selectionStart;
            const end = e.target.selectionEnd;
            const val = value || '';

            // Split by lines to find current context
            // But simpler logic requested: "A new line is created. The next incremental number is automatically added."
            // And "Numbering must continue sequentially (1, 2, 3...)"

            // Let's just append "\n{nextNumber}. "

            // Find the last number used? Or just count lines?
            // "Rules: Numbering must continue sequentially"
            // If user deletes a line, we don't re-number previous lines (per requirements).

            // Algorithm:
            // 1. Get text before cursor
            // 2. Get text after cursor
            // 3. Find/Calculate the next number. 
            //    Requirement says "Deleting a line must not auto-renumber previous lines".
            //    This implies we just take the next logical number based on the previous line or total lines?
            //    "The next incremental number is automatically added."

            // Let's try to determine the number of the CURRENT line, and add current+1? 
            // Or just count total lines? 
            // If I have:
            // 1. Text
            // 2. Text
            // And I press enter at end of line 2 -> 3. 

            // Simple implementation: Count newlines before cursor + 1? No, user might edit in middle.

            // Let's look at the last line's number if possible, or just index + 1.
            // If we assume standard format "X. ", we can try to parse.

            // Let's stick to the requested behavior example:
            // "1. Payment... [Enter] -> 2. "

            const lines = val.split('\n');
            // We want to insert a new line. 
            // What number should it have? 
            // If we are at the end, it's lines.length + 1.

            // Let's just find the max number currently in the text to avoid duplicates?
            // Or just line count. 
            // Requirement: "Deleting a line must not auto-renumber previous lines."
            // This implies numbers assume a persistent identity? Or just that we don't auto-fix them.

            // Let's use logic: Look at the line we are breaking from. 
            // If it starts with "N. ", next one is "N+1. ".
            // If not, maybe just "1. "? 

            const textBefore = val.substring(0, start);
            const textAfter = val.substring(end);

            const currentLineLastIndex = textBefore.lastIndexOf('\n');
            const currentLineStart = currentLineLastIndex === -1 ? 0 : currentLineLastIndex + 1;
            const currentLineContent = textBefore.substring(currentLineStart);

            const match = currentLineContent.match(/^(\d+)\./);
            let nextNum = 1;
            if (match) {
                nextNum = parseInt(match[1], 10) + 1;
            } else {
                // If current line doesn't have a number, maybe try to be smart or just 1.
                // If previous lines have numbers, we might want to follow.
                // For now, let's allow the user to manually number if they broke the chain, 
                // OR simpler: just lines.length if at end.

                // Let's go with the logic: If valid number found on current line, increment. Else 1.
                // Also check if textAfter starts with a number? 
            }

            const insertion = `\n${nextNum}. `;
            const newValue = textBefore + insertion + textAfter;

            if (newValue.length <= 1000) {
                onChange(newValue);
                // We need to schedule cursor update. 
                // Since we don't have easy access to setSelectionRange in parent's onChange, 
                // we might leave cursor at end of insertion which is default behavior for controlled input updates usually 
                // (cursor jumps to end). 
                // To fix cursor position:
                setTimeout(() => {
                    if (textareaRef.current) {
                        textareaRef.current.selectionStart = textareaRef.current.selectionEnd = start + insertion.length;
                    }
                }, 0);
            }
        }
    };

    return (
        <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2 flex justify-between">
                <span className="flex items-center">
                    <FileText className="w-4 h-4 mr-2" />
                    {label}
                </span>
                <span className="text-xs text-gray-400">
                    {(value || '').length}/1000 characters
                </span>
            </label>
            <textarea
                ref={textareaRef}
                value={value}
                onChange={handleChange}
                onFocus={handleFocus}
                onKeyDown={handleKeyDownEnhanced}
                placeholder={placeholder}
                className="w-full px-3 py-2 border border-blue-200 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors resize-none overflow-hidden min-h-[100px] text-sm leading-relaxed"
                style={{
                    whiteSpace: 'pre-wrap',
                    wordWrap: 'break-word'
                }}
            />
            <p className="text-xs text-gray-500 mt-1">
                Press Enter to add a new numbered line.
            </p>
        </div>
    );
};

export default TermsAndConditionsInput;
